import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Minus, ChevronUp, ChevronDown, Trash2 } from "lucide-react";
import { TargetCostRanges, DynamicRange } from "@/types/target-cost";
import { formatCurrency } from "@/utils/formatters";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useCallback } from "react";
import { updateRangeCalculations } from "@/utils/target-cost-calculations";

interface RangeFormProps {
  ranges: TargetCostRanges;
  onChange: (updates: Partial<TargetCostRanges>) => void;
  targetPrice?: number;
  actualCostPct?: number;
}

export function RangeForm({ ranges, onChange, targetPrice, actualCostPct }: RangeFormProps) {
  // Calculate range values
  const calculateRanges = useCallback(() => {
    if (!targetPrice || actualCostPct === undefined || targetPrice <= 0) return;

    const rangeDefinitions = ranges.dynamicRanges.map(range => ({
      from: range.startPercentage,
      to: range.endPercentage,
      share: range.sharePercentage
    }));

    const results = updateRangeCalculations({
      targetPrice,
      actualCostPct,
      ranges: rangeDefinitions
    });

    const updatedRanges = ranges.dynamicRanges.map((range, index) => ({
      ...range,
      amountInRange: results[index]?.amountInRange || 0,
      contractorShare: results[index]?.contractorShare || 0
    }));

    onChange({ ...ranges, dynamicRanges: updatedRanges });
  }, [targetPrice, actualCostPct, ranges.dynamicRanges, onChange]);

  useEffect(() => {
    calculateRanges();
  }, [calculateRanges]);

  // Range sequencing without gaps
  useEffect(() => {
    const { lessThanRange, greaterThanRange, dynamicRanges } = ranges;
    let updatedRanges = [...dynamicRanges];
    let hasChanges = false;

    updatedRanges = updatedRanges.map((range, index) => {
      let start = range.startPercentage;
      let end = range.endPercentage;

      // First dynamic range starts after less-than
      if (index === 0) {
        const expectedStart = lessThanRange.percentage;
        if (start !== expectedStart) {
          start = expectedStart;
          hasChanges = true;
        }
      } else {
        // Subsequent ranges start directly after previous
        const prevEnd = updatedRanges[index - 1].endPercentage;
        if (start !== prevEnd) {
          start = prevEnd;
          hasChanges = true;
        }
      }

      // Ensure valid range width (no overlap but no enforced gap)
      if (end <= start) {
        end = start + 1;
        hasChanges = true;
      }

      return { ...range, startPercentage: start, endPercentage: end };
    });

    // Update greater-than range
    const lastDynamic = updatedRanges[updatedRanges.length - 1];
    if (lastDynamic) {
      const newGTStart = lastDynamic.endPercentage;
      if (greaterThanRange.percentage !== newGTStart) {
        onChange({
          ...ranges,
          dynamicRanges: updatedRanges,
          greaterThanRange: { ...greaterThanRange, percentage: newGTStart }
        });
        return;
      }
    }

    if (hasChanges) {
      onChange({ ...ranges, dynamicRanges: updatedRanges });
    }
  }, [ranges, onChange]);

  const handleFixedRangeChange = (
    type: 'less-than' | 'greater-than',
    field: 'percentage' | 'sharePercentage',
    value: string
  ) => {
    const numValue = Math.max(0, Math.min(1000, Number(value) || 0));

    // Update first dynamic range when changing less-than percentage
    if (type === 'less-than' && field === 'percentage' && ranges.dynamicRanges[0]) {
      const updatedDynamic = ranges.dynamicRanges.map((r, i) => 
        i === 0 ? { ...r, startPercentage: numValue } : r
      );
      
      onChange({
        ...ranges,
        lessThanRange: { ...ranges.lessThanRange, [field]: numValue },
        dynamicRanges: updatedDynamic
      });
      return;
    }

    onChange({
      ...ranges,
      [type === 'less-than' ? 'lessThanRange' : 'greaterThanRange']: {
        ...ranges[type === 'less-than' ? 'lessThanRange' : 'greaterThanRange'],
        [field]: numValue
      }
    });
  };

  const handleDynamicRangeChange = (
    id: string,
    field: keyof Omit<DynamicRange, 'id'>,
    value: string
  ) => {
    const numValue = field === 'sharePercentage' 
      ? Math.max(0, Math.min(100, Number(value) || 0))
      : Math.max(0, Math.min(1000, Number(value) || 0));

    const currentIndex = ranges.dynamicRanges.findIndex(r => r.id === id);
    const updatedRanges = [...ranges.dynamicRanges];
    const currentRange = { ...updatedRanges[currentIndex], [field]: numValue };

    // Maintain continuity when changing end percentage
    if (field === 'endPercentage' && currentIndex < updatedRanges.length - 1) {
      updatedRanges[currentIndex + 1] = {
        ...updatedRanges[currentIndex + 1],
        startPercentage: numValue
      };
    }

    updatedRanges[currentIndex] = currentRange;
    onChange({ ...ranges, dynamicRanges: updatedRanges });
  };

  const addDynamicRange = () => {
    const lastRange = ranges.dynamicRanges[ranges.dynamicRanges.length - 1];
    const start = lastRange 
      ? lastRange.endPercentage
      : ranges.lessThanRange.percentage;

    const newRange: DynamicRange = {
      id: crypto.randomUUID(),
      startPercentage: start,
      endPercentage: start + 10,
      sharePercentage: 50,
      amountInRange: 0,
      contractorShare: 0
    };

    onChange({ 
      ...ranges, 
      dynamicRanges: [...ranges.dynamicRanges, newRange]
    });
  };

  const removeDynamicRange = (id: string) => {
    onChange({
      ...ranges,
      dynamicRanges: ranges.dynamicRanges.filter(range => range.id !== id)
    });
  };

  // Totals calculation
  const { totalAmountInRange, totalContractorShare, lessThanAmount, lessThanShare, greaterThanAmount, greaterThanShare } = useMemo(() => {
    const dynamicTotals = ranges.dynamicRanges.reduce((acc, range) => ({
      totalAmountInRange: acc.totalAmountInRange + (range.amountInRange || 0),
      totalContractorShare: acc.totalContractorShare + (range.contractorShare || 0),
    }), { totalAmountInRange: 0, totalContractorShare: 0 });

    let ltAmount = 0, ltShare = 0, gtAmount = 0, gtShare = 0;
    
    if (targetPrice && actualCostPct !== undefined) {
      if (actualCostPct < ranges.lessThanRange.percentage) {
        ltAmount = ((ranges.lessThanRange.percentage - actualCostPct) / 100) * targetPrice;
        ltShare = ltAmount * (ranges.lessThanRange.sharePercentage / 100);
      }

      if (actualCostPct > ranges.greaterThanRange.percentage) {
        gtAmount = ((actualCostPct - ranges.greaterThanRange.percentage) / 100) * targetPrice;
        gtShare = gtAmount * (ranges.greaterThanRange.sharePercentage / 100);
      }
    }

    return {
      totalAmountInRange: dynamicTotals.totalAmountInRange + ltAmount + gtAmount,
      totalContractorShare: dynamicTotals.totalContractorShare + ltShare + gtShare,
      lessThanAmount: ltAmount,
      lessThanShare: ltShare,
      greaterThanAmount: gtAmount,
      greaterThanShare: gtShare
    };
  }, [ranges, targetPrice, actualCostPct]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pain/Gain Share Ranges</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="relative overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left">Range</th>
                <th className="px-4 py-3 text-right">From (%)</th>
                <th className="px-4 py-3 text-right">To (%)</th>
                <th className="px-4 py-3 text-right">Share (%)</th>
                <th className="px-4 py-3 text-right">Amount in Range</th>
                <th className="px-4 py-3 text-right">Contractor's Share</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {/* Less Than Range */}
              <tr className="border-b bg-muted/30">
                <td className="px-4 py-3">Less Than</td>
                <td className="px-4 py-3 text-right">-</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="relative">
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={ranges.lessThanRange.percentage}
                        onChange={(e) => handleFixedRangeChange('less-than', 'percentage', e.target.value)}
                        className="w-24 text-right pr-8"
                        min={0}
                        max={1000}
                      />
                      <div className="absolute right-1 top-1 bottom-1 flex flex-col">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0"
                          onClick={() => handleFixedRangeChange('less-than', 'percentage', (ranges.lessThanRange.percentage + 1).toString())}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0"
                          onClick={() => handleFixedRangeChange('less-than', 'percentage', (ranges.lessThanRange.percentage - 1).toString())}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="relative">
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={ranges.lessThanRange.sharePercentage}
                        onChange={(e) => handleFixedRangeChange('less-than', 'sharePercentage', e.target.value)}
                        className="w-24 text-right pr-8"
                        min={0}
                        max={100}
                      />
                      <div className="absolute right-1 top-1 bottom-1 flex flex-col">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0"
                          onClick={() => handleFixedRangeChange('less-than', 'sharePercentage', (ranges.lessThanRange.sharePercentage + 1).toString())}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0"
                          onClick={() => handleFixedRangeChange('less-than', 'sharePercentage', (ranges.lessThanRange.sharePercentage - 1).toString())}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(lessThanAmount)}
                </td>
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(lessThanShare)}
                </td>
                <td className="px-4 py-3"></td>
              </tr>

              {/* Dynamic Ranges */}
              {ranges.dynamicRanges.map((range, index) => (
                <tr key={range.id} className={cn("border-b", index % 2 === 0 ? "bg-muted/30" : "bg-muted/10")}>
                  <td className="px-4 py-3">Range {index + 1}</td>
                  <td className="px-4 py-3 text-right">
                    <Input
                      value={range.startPercentage}
                      readOnly
                      className="w-24 text-right bg-muted/50"
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="relative">
                        <Input
                          type="number"
                          inputMode="decimal"
                          value={range.endPercentage}
                          onChange={(e) => handleDynamicRangeChange(range.id, 'endPercentage', e.target.value)}
                          className="w-24 text-right pr-8"
                          min={range.startPercentage}
                          max={1000}
                        />
                        <div className="absolute right-1 top-1 bottom-1 flex flex-col">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0"
                            onClick={() => handleDynamicRangeChange(range.id, 'endPercentage', (range.endPercentage + 1).toString())}
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0"
                            onClick={() => handleDynamicRangeChange(range.id, 'endPercentage', (range.endPercentage - 1).toString())}
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="relative">
                        <Input
                          type="number"
                          inputMode="decimal"
                          value={range.sharePercentage}
                          onChange={(e) => handleDynamicRangeChange(range.id, 'sharePercentage', e.target.value)}
                          className="w-24 text-right pr-8"
                          min={0}
                          max={100}
                        />
                        <div className="absolute right-1 top-1 bottom-1 flex flex-col">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0"
                            onClick={() => handleDynamicRangeChange(range.id, 'sharePercentage', (range.sharePercentage + 1).toString())}
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0"
                            onClick={() => handleDynamicRangeChange(range.id, 'sharePercentage', (range.sharePercentage - 1).toString())}
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {formatCurrency(range.amountInRange)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {formatCurrency(range.contractorShare)}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeDynamicRange(range.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}

              {/* Greater Than Range */}
              <tr className="border-b bg-muted/30">
                <td className="px-4 py-3">Greater Than</td>
                <td className="px-4 py-3 text-right">
                  <Input
                    value={ranges.greaterThanRange.percentage}
                    readOnly
                    className="w-24 text-right bg-muted/50"
                  />
                </td>
                <td className="px-4 py-3 text-right">-</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="relative">
                      <Input
                        type="number"
                        inputMode="decimal"
                        value={ranges.greaterThanRange.sharePercentage}
                        onChange={(e) => handleFixedRangeChange('greater-than', 'sharePercentage', e.target.value)}
                        className="w-24 text-right pr-8"
                        min={0}
                        max={100}
                      />
                      <div className="absolute right-1 top-1 bottom-1 flex flex-col">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0"
                          onClick={() => handleFixedRangeChange('greater-than', 'sharePercentage', (ranges.greaterThanRange.sharePercentage + 1).toString())}
                        >
                          <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0"
                          onClick={() => handleFixedRangeChange('greater-than', 'sharePercentage', (ranges.greaterThanRange.sharePercentage - 1).toString())}
                        >
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(greaterThanAmount)}
                </td>
                <td className="px-4 py-3 text-right font-medium">
                  {formatCurrency(greaterThanShare)}
                </td>
                <td className="px-4 py-3"></td>
              </tr>

              {/* Totals */}
              <tr className="bg-primary/5 font-medium">
                <td colSpan={4} className="px-4 py-3">Total</td>
                <td className="px-4 py-3 text-right">{formatCurrency(totalAmountInRange)}</td>
                <td className="px-4 py-3 text-right">{formatCurrency(totalContractorShare)}</td>
                <td className="px-4 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <Button
          variant="outline"
          onClick={addDynamicRange}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Range
        </Button>
      </CardContent>
    </Card>
  );
}